'use client'

// src/app/review/error.tsx
//
// Page-level error boundary for /review. Without this file, any
// render-time error in the review-cards page falls through to
// global-error.tsx ("Something went wrong / Our team has been
// notified") — which gives the user no context and no path back.
//
// This boundary catches the error, logs it to Sentry with enough
// metadata to triage, and renders a softer "Couldn't load your
// review session" card with a Back-to-Dashboard escape hatch.
//
// We intentionally don't wire `reset()` to the primary CTA because
// the most common cause is malformed data from /api/review/due —
// retrying just re-renders against the same bad state. Sending the
// user back to /dashboard breaks the loop; they can re-enter
// /review and the page's defensive card-filtering will skip the
// problem item.

import { useEffect } from 'react'
import Link from 'next/link'
import * as Sentry from '@sentry/nextjs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft } from 'lucide-react'

export default function ReviewError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    console.error('[review] page error:', error)
    Sentry.captureException(error, {
      tags: { surface: 'review' },
      // The digest is what's shown in production; pass it as an
      // extra so Sentry alerts can be cross-referenced with whatever
      // error ID the user sees on screen.
      extra: { digest: error.digest },
    })
  }, [error])

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-2xl px-4 py-8">
        <Card className="border-border/70 bg-card/60">
          <CardHeader>
            <CardTitle className="text-xl text-foreground">
              Couldn’t load your review session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Something went wrong loading your review cards. The error has been logged and we’ll
              look into it.
            </p>
            {error.digest ? (
              <p className="font-mono text-xs text-muted-foreground">Error ID: {error.digest}</p>
            ) : null}
            <div className="flex gap-3">
              <Button asChild variant="outline" className="border-border text-muted-foreground">
                <Link href="/dashboard">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboard
                </Link>
              </Button>
              <Button onClick={() => reset()} className="bg-brand text-on-brand">
                Try again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
